<template>
    <Head :title="'Sell Your Home in St. Johns County'" >
        <link rel="canonical" href="https://www.ginabetbeze.com/selling" />
        <meta name="description" content="Sell your home in St. Johns County with Gina Betbeze. Leverage her Julington Creek expertise for top pricing and marketing to maximize your sale." />
    </Head>
    <!-- Simple Blurb -->

    <PageContent
        :id="'selling-page'"
        :breadcrumbs="['Home','Selling']"
        :header="'Seller Form'"
    >
        <BasicContainer>
            <FormContainer>
                <form @submit.prevent="submitVelocityForm">

                    <VelocityFormAnimation ref="formAnimation"
                                           :paperTitle="'Seller Form'">
                        <div>
                            <FieldGroup>
                                <div  class="w-full text-center">
                                    <h2 id="contact-head" class="text-3xl font-subtitle">
                                        Enter your details, and we'll find you some buyers.
                                    </h2>
                                </div>
                            </FieldGroup>
                            <FieldGroup>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="sellingForm.firstname"
                                        :name="`firstname`"
                                        :label="'First Name'"
                                        :type="`text`"
                                        required
                                    />
                                </div>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="sellingForm.lastname"
                                        :name="`lastname`"
                                        :label="'Last Name'"
                                        :type="`text`"
                                        required
                                    />
                                </div>
                            </FieldGroup>

                            <FieldGroup>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="sellingForm.email"
                                        :name="`email`"
                                        :label="'Email'"
                                        :type="`email`"
                                        required
                                    />
                                </div>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="sellingForm.phone"
                                        :name="`phone`"
                                        :label="'Phone (optional)'"
                                        :type="`tel`"
                                    />
                                </div>
                            </FieldGroup>
                            <FieldGroup>
                                <div class="w-full">
                                    <Input
                                        v-model="sellingForm.homeAddress"
                                        :label="'Home Address'"
                                        :type="`text`"
                                        required
                                        :placeholder="`Enter your address here`"
                                    />
                                </div>
                            </FieldGroup>

                            <FieldGroup>
                                <Radio
                                    v-model="sellingForm.homeType"
                                    type="text"
                                    label="Home Type"
                                    required
                                    :label="'Home Type'"
                                    :options="[
                                                        { value: 'single-family', label: 'Single-Family Home' },
                                                        { value: 'condo', label: 'Condominium' },
                                                        { value: 'townhouse', label: 'Townhouse' },
                                                        { value: 'multi-family', label: 'Multi-Family Home' },
                                                    ]"
                                    :name="homeType"
                                />
                            </FieldGroup>

                            <FieldGroup>
                                <Radio
                                    v-model="sellingForm.timeFrame"
                                    type="text"
                                    label="Time Frame"
                                    required
                                    :label="'Price Range'"
                                    :options="[
                                { value: '0-3months', label: '0 - 3 Months' },
                                { value: '3-6months', label: '3 - 6 Months' },
                                { value: '6-12months', label: '12+ Months' },
                            ]"
                                    :name="`timeFrame`"
                                />
                            </FieldGroup>

                            <div class="w-full flex justify-center">
                                <Button class="max-w-96">Submit</Button>
                            </div>
                        </div>
                    </VelocityFormAnimation>

                </form>
            </FormContainer>
        </BasicContainer>
    </PageContent>
</template>
<script setup>
import { Head, useForm, usePage } from "@inertiajs/vue3";
import PageContent from "../Components/Layout/PageContent.vue";
import HomeLayout from "../Layouts/HomeLayout.vue";
import Input from "../Components/Forms/Input.vue";
import Radio from "../Components/Forms/Radio.vue";
import Button from "../Components/Forms/Button.vue";
import Breadcrumbs from "@/Components/PageContent/Breadcrumbs.vue";
import MainHead from "@/Components/PageContent/MainHead.vue";
import BasicContainer from "@/Components/PageContent/Containers/BasicContainer.vue";
import FormContainer from "@/Components/PageContent/Containers/FormContainer.vue";
import FieldGroup from "@/Components/Forms/FieldGroup.vue";
import InfoAlert from "@/Components/PageContent/InfoAlert.vue";
import VelocityFormAnimation from "@/Components/Forms/VelocityFormAnimation.vue";

import { route } from "ziggy-js";
import { Inertia } from "@inertiajs/inertia";
import { ref } from "vue";

defineOptions({ layout: HomeLayout });
const page = usePage();
const props = defineProps({
    title: {
        type: String,
        required: true,
    },
});
const formAnimation = ref(null);

const sellingForm = useForm({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    homeType: "", // dropdown
    timeFrame: "", // dropdown
    homeAddress: "",
});
const submitVelocityForm = async (e) => {
    e.preventDefault();
    let formKey = 'selling';
    let redirectKey = "/selling";
    // Collect the submission data in a json string for insertion into submission table
    const jsonData = JSON.stringify(sellingForm.data());

    const velocitySubmissionForm = useForm({
        form_key: formKey,
        field_values: jsonData,
        redirect: redirectKey,
        recaptcha_token: ""
    });

    try {
        // Execute reCAPTCHA
        velocitySubmissionForm.recaptcha_token  = await grecaptcha.enterprise.execute('6LeDhtAqAAAAAJm-0AUDswv8xmB2K8knAmdzyDHi', { action: 'SUBMIT' });
    } catch (error) {
        console.error('reCAPTCHA error:', error);
    }

    velocitySubmissionForm.post( route('velocitySubmission.submitPublic', formKey ),{
        preserveState: true,
        preserveScroll: true,

        onError: (errors) => {
            // Log the detailed error object to the console for debugging
            console.error('Error Details:', errors);

            // Construct a detailed error message if the errors object contains specific error messages
            let detailedErrorMessage = 'There was an error submitting the form "' + page.props.velocityForm.title +
                '".';
            if (errors && typeof errors === 'object' && Object.keys(errors).length > 0) {
                // Append each error message to the detailed error message
                Object.keys(errors).forEach((key) => {
                    detailedErrorMessage += ` ${key}: ${errors[key]}`;
                });
            }
            formAnimation.value.toggleOnEnvelopeFailAnimation(detailedErrorMessage);

            // Display the detailed error message in the UI
            page.props.messages.push({
                message: detailedErrorMessage,
                duration: 10,
                type: "error"
            });
            console.log(detailedErrorMessage);
        },
        onSuccess: () => {
            formAnimation.value.toggleOnEnvelopeSentAnimation();

            setTimeout(() => {
                sellingForm.reset();
                Inertia.visit(route(route().current()));
            }, 7500)
        },
    });
};
</script>
